// @ts-nocheck
import { ReactNode } from 'react';
import { Box, BackgroundImage } from '@mantine/core';
import { useMantineTheme, useMantineColorScheme } from '@mantine/core';
import { Center, Container, Grid, Stack } from '@/components/ui/layout';
import { Card, Paper } from '@/components/ui/data-display';

export type LayoutVariant = 'simple' | 'split' | 'gradient' | 'modern' | 'cover';

interface AuthLayoutWrapperProps {
  variant?: LayoutVariant;
  children: ReactNode;
  splitContent?: ReactNode;
  backgroundImage?: string;
  gradientFrom?: string;
  gradientTo?: string;
  gradientDegree?: number;
  maxWidth?: 'xs' | 'sm' | 'md';
  cardPadding?: 'md' | 'lg' | 'xl';
  glassEffect?: boolean;
}

export default function AuthLayoutWrapper({
  variant = 'simple',
  children,
  splitContent,
  backgroundImage,
  gradientFrom,
  gradientTo,
  gradientDegree = 135,
  maxWidth = 'xs',
  cardPadding = 'xl',
  glassEffect = false,
}: AuthLayoutWrapperProps) {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';

  const defaultGradientFrom = isDark ? theme.colors.indigo[9] : theme.colors.indigo[6];
  const defaultGradientTo = isDark ? theme.colors.cyan[9] : theme.colors.cyan[4];

  const getGlassStyles = () => ({
    backgroundColor: isDark ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(12px)',
    border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
  });

  // Simple Layout - Centered card on subtle background
  if (variant === 'simple') {
    return (
      <Box
        style={{
          minHeight: '100vh',
          backgroundColor: isDark ? theme.colors.dark[8] : theme.colors.gray[0],
        }}
      >
        <Center mih="100vh" p="md">
          <Container size={maxWidth} w="100%">
            <Card variant="elevated" p={cardPadding} radius="lg">
              {children}
            </Card>
          </Container>
        </Center>
      </Box>
    );
  }

  // Split Layout - Two column with image/illustration on one side
  if (variant === 'split') {
    return (
      <Box style={{ minHeight: '100vh', display: 'flex' }}>
        {/* Left side - Image/Content */}
        <Box
          style={{
            width: '50%',
            background: `linear-gradient(${gradientDegree}deg, ${gradientFrom || defaultGradientFrom}, ${gradientTo || defaultGradientTo})`,
            position: 'relative',
            overflow: 'hidden',
          }}
          visibleFrom="md"
        >
          {backgroundImage && (
            <BackgroundImage
              src={backgroundImage}
              style={{
                position: 'absolute',
                inset: 0,
                opacity: 0.3,
              }}
            />
          )}
          <Center h="100%" p="xl" style={{ position: 'relative', zIndex: 1 }}>
            {splitContent || (
              <Stack align="center" gap="lg">
                <Box
                  style={{
                    width: 120,
                    height: 120,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Box
                    style={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(255,255,255,0.3)',
                    }}
                  />
                </Box>
              </Stack>
            )}
          </Center>
        </Box>

        {/* Right side - Form */}
        <Box
          style={{
            flex: 1,
            backgroundColor: isDark ? theme.colors.dark[7] : theme.colors.white,
          }}
        >
          <Center mih="100vh" p="xl">
            <Container size={maxWidth} w="100%">
              {children}
            </Container>
          </Center>
        </Box>
      </Box>
    );
  }

  // Gradient Layout - Full gradient background with glass card
  if (variant === 'gradient') {
    return (
      <Box
        style={{
          minHeight: '100vh',
          background: `linear-gradient(${gradientDegree}deg, ${gradientFrom || defaultGradientFrom}, ${gradientTo || defaultGradientTo})`,
        }}
      >
        <Center mih="100vh" p="md">
          <Container size={maxWidth} w="100%">
            <Card
              p={cardPadding}
              radius="lg"
              style={glassEffect ? getGlassStyles() : undefined}
            >
              {children}
            </Card>
          </Container>
        </Center>
      </Box>
    );
  }

  // Modern Layout - Geometric patterns with floating elements
  if (variant === 'modern') {
    return (
      <Box
        style={{
          minHeight: '100vh',
          backgroundColor: isDark ? theme.colors.dark[8] : theme.colors.gray[0],
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background pattern */}
        <Box
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `radial-gradient(${isDark ? theme.colors.dark[6] : theme.colors.gray[2]} 1px, transparent 1px)`,
            backgroundSize: '20px 20px',
            opacity: 0.5,
          }}
        />

        {/* Floating shapes */}
        <Box
          style={{
            position: 'absolute',
            top: '10%',
            right: '10%',
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${theme.colors.indigo[4]}, ${theme.colors.cyan[4]})`,
            opacity: 0.2,
            filter: 'blur(40px)',
          }}
        />
        <Box
          style={{
            position: 'absolute',
            bottom: '15%',
            left: '5%',
            width: 150,
            height: 150,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${theme.colors.violet[4]}, ${theme.colors.pink[4]})`,
            opacity: 0.2,
            filter: 'blur(40px)',
          }}
        />

        <Center mih="100vh" p="md" style={{ position: 'relative', zIndex: 1 }}>
          <Container size={maxWidth} w="100%">
            <Card variant="elevated" p={cardPadding} radius="lg">
              {children}
            </Card>
          </Container>
        </Center>
      </Box>
    );
  }

  // Cover Layout - Full background image with overlay
  if (variant === 'cover') {
    return (
      <Box
        style={{
          minHeight: '100vh',
          position: 'relative',
          backgroundImage: backgroundImage ? `url(${backgroundImage})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundColor: isDark ? theme.colors.dark[8] : theme.colors.gray[8],
        }}
      >
        {/* Overlay */}
        <Box
          style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: isDark ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.5)',
          }}
        />

        <Center mih="100vh" p="md" style={{ position: 'relative', zIndex: 1 }}>
          <Container size={maxWidth} w="100%">
            <Card
              p={cardPadding}
              radius="lg"
              style={{
                backgroundColor: isDark ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(8px)',
              }}
            >
              {children}
            </Card>
          </Container>
        </Center>
      </Box>
    );
  }

  return null;
}
